import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';

import { getSupplier, getSupplierFromUserId, selectSupplierState } from '../../store/entities/supplier';
import { supplierEmployeeListActions } from '../../store/entities/supplierEmployeeList';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { Input } from '../common/form/Input';
import { SubmitButton } from '../common/form/SubmitButton';
import { SupplierEmployeeRoleSelect } from '../common/form/SupplierEmployeeRoleSelect';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import {
  getSupplierEmployee,
  selectSupplierEmployee,
  selectSupplierEmployeeState,
  supplierEmployeeActions,
} from '../../store/entities/supplierEmployee';
import { LanguageSelect } from '../common/form/LanguageSelect';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { PhoneInput } from '../common/form/PhoneInput';
import { hasRole } from '../../utils/roles';
import { selectIdentity } from '../../store/entities/identity';
import { useBack } from '../../hooks/useBack';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { UserRole } from '@api/models/enums/UserRole';
import { supplierEmployeeService } from '@api/services/services';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';
import { ShowResourceContent } from '../ShowResourceContent';

export const SupplierEmployeeFromVisitorDetail: FC = () => {
  const { t } = useTranslation();
  const { supplierId, supplierEmployeeId } = useParams();
  const identity = useAppSelector(selectIdentity);
  const { backTo, goBack } = useBack(hasRole(identity.roles, [UserRole.ExternalManager]) ? '/supplier' : `/supplier/${supplierId}`);
  const supplierEmployee = useAppSelector(selectSupplierEmployee);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const dispatch = useAppDispatch();
  const [currentRole, setRole] = useState(supplierEmployee.role);

  useEffect(() => {
    setRole(supplierEmployee.role);
  }, [supplierEmployee.role]);

  useEffect(() => {
    if (supplierEmployeeId === 'new') {
      dispatch(supplierEmployeeActions.default());
    } else {
      dispatch(getSupplierEmployee(supplierEmployeeId!));
    }
    setRole(supplierEmployee.role);

    if (hasRole(identity.roles, [UserRole.ExternalManager])) {
      dispatch(getSupplierFromUserId(identity.id));
    } else {
      dispatch(getSupplier(supplierId!));
    }
  }, [supplierEmployeeId, supplierId, dispatch, identity.roles, identity.id]);

  const handleSubmit = async (data: SupplierEmployeeDto) => {
    if (currentRole === UserRole.ExternalManager && data.email === '') data.email = undefined;

    await supplierEmployeeService.updateVisitorToSupplierEmployee(supplierId!, supplierEmployeeId!, data);
    dispatch(supplierEmployeeListActions.reload(true));
    goBack();
  };

  return (
    <>
      <DetailHeader title="supplierEmployee.detailTitle" backTo={backTo} />
      <ShowResourceContent
        resource={[resources.getSupplier, resources.getSupplierFromUserId, resources.getSupplierEmployee]}
        fetchStateSelector={[selectSupplierState, selectSupplierEmployeeState]}
      >
        <FormikDebounce<SupplierEmployeeDto>
          initialValues={{
            ...supplierEmployee,
            // @ts-expect-error role must be empty for visitor conversion, not a valid UserRole
            role: undefined,
            language: supplierEmployeeId === 'new' ? (companyCustomization?.defaultLanguage ?? '') : supplierEmployee.language,
          }}
          validateOnChange={true}
          validationSchema={Yup.object().shape({
            name: validations.stringRequired(t),
            lastName: validations.stringRequired(t),
            phone: validations.phoneOptional(t),
            email: currentRole === UserRole.ExternalManager ? validations.emailRequired(t) : validations.emailOptional(t),
            language: validations.stringRequired(t),
            role: validations.stringRequired(t),
          })}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              <Input name="name" label={t('supplierEmployee.name')} maxLength={100} required />
              <Input name="lastName" label={t('supplierEmployee.lastName')} maxLength={100} required />

              <SupplierEmployeeRoleSelect onRoleChange={setRole} name="role" label={t('supplierEmployee.role')} required />
              <PhoneInput name="phone" label={t('supplierEmployee.phone')} />
              <Input
                name="email"
                removeSpaces
                label={t('supplierEmployee.email')}
                maxLength={100}
                required={currentRole === UserRole.ExternalManager}
              />
              <LanguageSelect
                name="language"
                label={t('user.language')}
                languages={SUPPORTED_LANGUAGES}
                allowedLanguages={companyCustomization?.applicationLanguages}
                required
              />

              <ShowError resource={resources.updateVisitorToSupplierEmployee} />
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </ShowResourceContent>
    </>
  );
};
